import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  Button,
  ColorsV2,
  Divider,
  Flex,
  Header,
  Select,
  Skeleton,
  Text,
  getToken,
  spaces,
  useThemeColors,
} from '@electricjs/arc';
import {
  EmployeeLeaveStatusEnum,
  EmployeeStatusEnum,
} from '@electricjs/core_entity-client';
import { useEmployeeDirectory } from '@/hooks/useEmployeeDirectory';
import { useGetOrganizationApplicationChampionsBulkQuery } from '@/redux/slices/organizationApiSlice';
import { useGetBulkEmployeeLeavesQuery } from '@/redux/slices/employeeApiSlice';
import { useGetRequestTypeCategoryChampionsV2Query } from '@/redux/slices/requestApiSlice';
import ExternalLink from 'common/ExternalLink';
import { getDisplayFullName } from 'common/utils/getFullName';
import { isEmployeeOnLeave } from '@/helpers/isEmployeeOnLeave';
import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import EmployeeOnLeaveIcon from '@/components/People/EmployeeOnLeaveIcon';
import type { RequestTypeCategory } from '@/types/requests';
import type { Employee, EmployeeDirectoryListing } from '@/types/employees';
import type { ChampionOption } from '@/pages/Organization/Settings/ChampSelectorV2';
import type { OrganizationApplication } from '@/types/applications';

const { space_xs, space_s, space_m, space_l } = spaces;

type Props = {
  categories: RequestTypeCategory[];
  applications: OrganizationApplication[];
  employee: Employee;
  organizationId: string;
  onNext: (reassignChampToEmployeeId: string | null) => void;
  onBack: () => void;
};

const GRID_COLUMNS = '2fr 1fr';

const GridContainer = styled.div`
  border: 1px solid ${getToken(ColorsV2.BORDER)};
  border-radius: 0.8rem;
  width: 100%;
  overflow: hidden;
  background: ${getToken(ColorsV2.BACKGROUND_ALT)};
`;

const GridRow = styled.div<{ $stripe?: boolean }>`
  display: grid;
  grid-template-columns: ${GRID_COLUMNS};
  gap: ${space_m};
  align-items: center;
  padding: ${space_m};
  box-sizing: border-box;
  background: ${({ $stripe }) =>
    $stripe ? getToken(ColorsV2.BACKGROUND) : 'transparent'};
`;

const HeaderRow = styled(GridRow)`
  background: ${getToken(ColorsV2.BACKGROUND_ALT)};
  padding: ${space_s} ${space_m};
  border-bottom: 1px solid ${getToken(ColorsV2.BORDER)};
`;

type CategoryRowProps = {
  cat: RequestTypeCategory;
  index: number;
  employeeId: string;
  organizationId: string;
  textLightColor: string;
  t: (key: string) => string;
};

const CategoryRow = ({
  cat,
  index,
  employeeId,
  organizationId,
  textLightColor,
  t,
}: CategoryRowProps) => {
  const { data: catChampions, isLoading: isLoadingCatChamps } =
    useGetRequestTypeCategoryChampionsV2Query({
      organizationId,
      requestTypeCategoryId: cat.id,
    });

  const isPrimary =
    catChampions?.find(c => c.employeeId === employeeId)?.rank === 0;

  return (
    <div>
      {index > 0 && <Divider />}
      <GridRow $stripe={index % 2 === 0}>
        <Flex vertical gap={space_xs}>
          <Text variant="label-small" color={textLightColor}>
            {t('offboardings.championReassignment.sectionCategory')}
          </Text>
          <Text>{cat.name}</Text>
        </Flex>
        {isLoadingCatChamps ? (
          <Skeleton width="6rem" height="1.6rem" />
        ) : (
          <Text color={textLightColor}>
            {t(
              isPrimary
                ? 'offboardings.championReassignment.rolePrimary'
                : 'offboardings.championReassignment.roleSecondary'
            )}
          </Text>
        )}
      </GridRow>
    </div>
  );
};

const ChampionReassignment = ({
  categories,
  applications,
  employee,
  organizationId,
  onNext,
  onBack,
}: Props) => {
  const { t } = useTranslation('common');
  const { ithEmployeeLeave } = useFlags();
  const [textLightColor] = useThemeColors([ColorsV2.TEXT_LIGHT]);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null
  );

  const { data: employees, isFetching: isFetchingEmployees } =
    useEmployeeDirectory({
      organizationId,
      status: EmployeeStatusEnum.Active,
    });

  const applicationKeys = useMemo(
    () => applications.map(a => a.application.slug),
    [applications]
  );

  const { data: appChampsData, isLoading: isLoadingAppChamps } =
    useGetOrganizationApplicationChampionsBulkQuery(
      { organizationId, applicationKeys },
      { skip: applicationKeys.length === 0 }
    );

  const candidateEmployees = useMemo(
    () =>
      sortBy(
        (employees ?? []).filter(
          (e): e is EmployeeDirectoryListing =>
            !!e.email && e.id !== employee.id
        ),
        e => getDisplayFullName(e).toLowerCase()
      ),
    [employees, employee.id]
  );

  const employeeIds = useMemo(
    () => candidateEmployees.map(e => e.id),
    [candidateEmployees]
  );

  const {
    data: employeeLeavesData,
    isLoading: isLoadingLeaves,
    isError: isErrorLeaves,
  } = useGetBulkEmployeeLeavesQuery(
    ithEmployeeLeave && employeeIds.length
      ? {
          organizationId,
          employeeIds,
          // InProgress powers the on-leave icon; Scheduled is also needed so
          // it can show the upcoming-leave indicator.
          statuses: [
            EmployeeLeaveStatusEnum.InProgress,
            EmployeeLeaveStatusEnum.Scheduled,
          ],
        }
      : skipToken
  );

  // Without this, a failed bulk fetch silently renders as "no one is on
  // leave" with no signal that the data is missing rather than empty.
  useEffect(() => {
    if (isErrorLeaves) {
      window?.DD_RUM?.addError('Failed to fetch bulk employee leave status', {
        location: 'ChampionReassignment',
      });
    }
  }, [isErrorLeaves]);

  const championOptions = useMemo<ChampionOption[]>(
    () =>
      candidateEmployees.map(e => ({
        employeeId: e.id,
        employee: e,
        label: getDisplayFullName(e),
        employeeLeaves: employeeLeavesData?.[e.id] ?? [],
      })),
    [candidateEmployees, employeeLeavesData]
  );

  const isOptionDisabled = (option: ChampionOption) =>
    isEmployeeOnLeave(option.employeeLeaves);

  const selectedOption =
    championOptions.find(o => o.employeeId === selectedEmployeeId) ?? null;

  const handleNext = () => {
    onNext(selectedEmployeeId);
  };

  const employeeName = getDisplayFullName(employee);

  return (
    <>
      <Header
        title={t('offboardings.championReassignment.title')}
        subtitle={t('offboardings.championReassignment.subtitle', {
          employeeName,
        })}
      />

      <GridContainer>
        <HeaderRow>
          <Text variant="label-large">
            {t('offboardings.championReassignment.columnName')}
          </Text>
          <Text variant="label-large">
            {t('offboardings.championReassignment.columnRole')}
          </Text>
        </HeaderRow>

        {categories.map((cat, i) => (
          <CategoryRow
            key={cat.id}
            cat={cat}
            index={i}
            employeeId={employee.id}
            organizationId={organizationId}
            textLightColor={textLightColor}
            t={t}
          />
        ))}

        {applications.map((app, i) => {
          const globalIndex = categories.length + i;
          const appChamps =
            appChampsData?.organizationApplicationChampions[
              app.application.slug
            ] ?? [];
          const isPrimary =
            appChamps.find(c => c.employeeId === employee.id)?.rank === 0;

          return (
            <div key={app.id}>
              {(i > 0 || categories.length > 0) && <Divider />}
              <GridRow $stripe={globalIndex % 2 === 0}>
                <Flex vertical gap={space_xs}>
                  <Text variant="label-small" color={textLightColor}>
                    {t('offboardings.championReassignment.sectionApplication')}
                  </Text>
                  <Flex vAlignContent="center" gap={space_s}>
                    <ApplicationIcon
                      name={app.application.name}
                      icon={app.application.icon}
                      size="2.4rem"
                    />
                    <Text>{app.application.name}</Text>
                  </Flex>
                </Flex>
                {isLoadingAppChamps ? (
                  <Skeleton width="6rem" height="1.6rem" />
                ) : (
                  <Text color={textLightColor}>
                    {t(
                      isPrimary
                        ? 'offboardings.championReassignment.rolePrimary'
                        : 'offboardings.championReassignment.roleSecondary'
                    )}
                  </Text>
                )}
              </GridRow>
            </div>
          );
        })}
      </GridContainer>

      <Flex vertical gap={space_s} mt={space_l} maxWidth="45rem" width="100%">
        <Text variant="label-large">
          {t('offboardings.championReassignment.selectLabel')}
        </Text>
        <Select
          id="champion-replacement-select"
          name="champion-replacement-select"
          placeholder={t('offboardings.championReassignment.selectPlaceholder')}
          options={championOptions}
          value={selectedOption}
          onChange={option => {
            const opt = option as ChampionOption | null;
            setSelectedEmployeeId(opt?.employeeId ?? null);
          }}
          isSearchable
          isClearable={false}
          isLoading={isFetchingEmployees}
          // Until the leave check itself resolves, employeeLeaves defaults to
          // [] for every option (see isOptionDisabled below), which would let
          // an on-leave employee be picked in that window. Disable the whole
          // select rather than let that race through.
          disabled={isLoadingLeaves}
          getOptionValue={(o: ChampionOption) => o.employeeId}
          isOptionDisabled={isOptionDisabled}
          formatOptionLabel={(o: ChampionOption) => (
            <Flex alignItems="center">
              <EmployeeOnLeaveIcon
                employeeId={o.employeeId}
                employeeStatus={o.employee?.status}
                leaves={o.employeeLeaves}
              />
              {o.label}
            </Flex>
          )}
        />
      </Flex>

      <Flex alignItems="center" gap="0.4rem" mt={space_s} flexWrap="wrap">
        <Text variant="body-2" color={textLightColor}>
          {t('offboardings.championReassignment.settingsPrompt')}
        </Text>
        <ExternalLink
          id="champion-reassignment-settings-link"
          href="/settings/category-champions"
          variant="body-2"
          showIcon={false}>
          {t('offboardings.championReassignment.settingsLinkLabel')}
        </ExternalLink>
      </Flex>

      <Flex mt="9rem" hAlignContent="right" gap="3.4rem" alignSelf="flex-end">
        <Button
          id="champion-reassignment-back-button"
          variant="text"
          onClick={onBack}>
          {t('offboardings.championReassignment.backButton')}
        </Button>
        <Button
          id="champion-reassignment-next-button"
          disabled={!selectedEmployeeId}
          onClick={handleNext}>
          {t('offboardings.championReassignment.nextButton')}
        </Button>
      </Flex>
    </>
  );
};

export default ChampionReassignment;
